import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import api from '../services/api.js';
import QuestionImage from '../components/QuestionImage.jsx';
import { useAuth } from '../App.jsx';
import OnboardingModal from '../components/OnboardingModal.jsx';
import ModalClose from '../components/ModalClose.jsx';

function Confetti({ active }) {
  const canvasRef = useRef(null);
  const particles = useRef([]);
  const raf = useRef(null);
  useEffect(() => {
    if (!active || !canvasRef.current) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;
    const colors = ['#f2c063','#ff7a6b','#19b79e','#51cf66','#ff922b','#cc5de8','#22b8cf'];
    for (let i = 0; i < 60; i++) {
      particles.current.push({
        x: Math.random() * canvas.width, y: -20 - Math.random() * 100,
        w: 6 + Math.random() * 6, h: 4 + Math.random() * 4,
        vx: (Math.random() - 0.5) * 4, vy: 2 + Math.random() * 4,
        rot: Math.random() * 360, rotV: (Math.random() - 0.5) * 10,
        color: colors[Math.floor(Math.random() * colors.length)], life: 1,
      });
    }
    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      particles.current = particles.current.filter(p => p.life > 0);
      particles.current.forEach(p => {
        p.x += p.vx; p.y += p.vy; p.vy += 0.1; p.rot += p.rotV; p.life -= 0.008;
        ctx.save(); ctx.translate(p.x, p.y); ctx.rotate(p.rot * Math.PI / 180);
        ctx.globalAlpha = p.life; ctx.fillStyle = p.color;
        ctx.fillRect(-p.w/2, -p.h/2, p.w, p.h); ctx.restore();
      });
      if (particles.current.length > 0) raf.current = requestAnimationFrame(animate);
    };
    raf.current = requestAnimationFrame(animate);
    return () => { if (raf.current) cancelAnimationFrame(raf.current); particles.current = []; };
  }, [active]);
  if (!active) return null;
  return <canvas ref={canvasRef} style={{ position:'fixed',top:0,left:0,pointerEvents:'none',zIndex:100 }} />;
}

function StreakBadge({ streak }) {
  if (streak < 2) return null;
  const fire = streak >= 5 ? '🔥🔥🔥' : streak >= 3 ? '🔥🔥' : '🔥';
  const label = streak >= 7 ? 'LÉGENDAIRE' : streak >= 5 ? 'EN FEU' : streak >= 3 ? 'COMBO' : 'SÉRIE';
  return (
    <motion.div className="streak-badge" initial={{ scale:0,rotate:-20 }} animate={{ scale:1,rotate:0 }}
      key={streak} transition={{ type:'spring',damping:8,stiffness:200 }}>
      <span className="streak-fire">{fire}</span>
      <span className="streak-count">{streak}</span>
      <span className="streak-label">{label}</span>
    </motion.div>
  );
}

function TimerBar({ active, duration = 25, onExpire }) {
  const [elapsed, setElapsed] = useState(0);
  const interval = useRef(null);
  const expired = useRef(false);
  useEffect(() => {
    setElapsed(0);
    expired.current = false;
    if (!active) return;
    interval.current = setInterval(() => {
      setElapsed(prev => {
        if (prev >= duration && !expired.current) {
          expired.current = true;
          if (onExpire) setTimeout(onExpire, 0);
          return duration;
        }
        return prev >= duration ? duration : prev + 0.05;
      });
    }, 50);
    return () => clearInterval(interval.current);
  }, [active, duration, onExpire]);
  const pct = Math.max(0, 1 - elapsed / duration);
  const color = pct > 0.5 ? '#51cf66' : pct > 0.25 ? '#ff922b' : '#ff7a6b';
  return (
    <div className="timer-bar-container">
      <div className="timer-bar-fill" style={{ width:`${pct*100}%`, background:color }} />
      <span className="timer-bar-text" style={{ color }}>{Math.max(0,Math.ceil(duration-elapsed))}s</span>
    </div>
  );
}

export default function Quiz() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, setUser } = useAuth();
  const hasTimer = user?.has_timer !== false;  // default true
  const audioOn = user?.audio_enabled === true; // lecture audio AUTO (TTS) — OFF par défaut
  // Préférence d'affichage/lecture de la fiche selon bonne/mauvaise réponse (local, défaut ON).
  const ficheVisibleFor = (isCorrect) => isCorrect
    ? localStorage.getItem('show_card_on_correct') !== '0'
    : localStorage.getItem('show_card_on_wrong') !== '0';
  const [session, setSession] = useState(location.state?.session || null);
  const [question, setQuestion] = useState(location.state?.session?.current_question || null);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(!question);
  const [questionKey, setQuestionKey] = useState(0);
  const [questionStart, setQuestionStart] = useState(Date.now());
  const [streak, setStreak] = useState(0);
  const [bestStreak, setBestStreak] = useState(0);
  const [showConfetti, setShowConfetti] = useState(false);
  const [totalPoints, setTotalPoints] = useState(0);
  const [lastPoints, setLastPoints] = useState(null);
  const [shakeWrong, setShakeWrong] = useState(false);
  const [newConnections, setNewConnections] = useState([]);
  const [showConnectionPopup, setShowConnectionPopup] = useState(false);
  const [connIndex, setConnIndex] = useState(0);
  const [showAdaptiveTip, setShowAdaptiveTip] = useState(false);
  const [glowKey, setGlowKey] = useState(null);        // choix en cours de lecture (glow)
  const [ttsReading, setTtsReading] = useState(false); // lecture audio de la question en cours
  const [completedMissions, setCompletedMissions] = useState([]);
  const [newAchievements, setNewAchievements] = useState([]);
  const [timedOut, setTimedOut] = useState(false);
  const [reportSent, setReportSent] = useState(false);
  const [reporting, setReporting] = useState(false);
  const [showReportInput, setShowReportInput] = useState(false);
  const [reportReason, setReportReason] = useState('');
  const handleAnswerRef = useRef(null);
  const ttsTokenRef = useRef(0);
  const ttsAudioRef = useRef(null);

  const submitReport = async () => {
    const reason = reportReason.trim();
    if (!question?.id || reporting || reportSent || !reason) return;
    setReporting(true);
    try {
      await api.reportQuestion(question.id, reason);
      setReportSent(true);
      setShowReportInput(false);
    } catch (e) { /* silencieux : ne pas perturber le quiz */ }
    setReporting(false);
  };

  // Tip "difficulté adaptative" au tout premier quiz (flag serveur, cross-device).
  useEffect(() => {
    if (user && user.seen_adaptive_tip === false) setShowAdaptiveTip(true);
  }, [user]);

  const dismissAdaptiveTip = async () => {
    setShowAdaptiveTip(false);
    if (user) setUser({ ...user, seen_adaptive_tip: true });
    try { await api.updateProfile({ seen_adaptive_tip: true }); } catch (e) { /* non bloquant */ }
  };

  // ── Lecture audio des questions (TTS pré-généré, voix Denise) ──
  const ttsBase = () => (question?.qid ? `/tts/${question.qid}` : null);
  const stopTts = () => {
    ttsTokenRef.current++;
    if (ttsAudioRef.current) { try { ttsAudioRef.current.pause(); } catch (e) {} }
    setGlowKey(null); setTtsReading(false);
  };
  // Résout à true si le clip a bien joué, false si absent (404) ou autoplay bloqué.
  const playClip = (src) => new Promise((resolve) => {
    const a = new Audio(src);
    ttsAudioRef.current = a;
    a.onended = () => resolve(true);
    a.onerror = () => resolve(false);      // clip absent (404) → échec, on n'enchaîne pas
    a.play().catch(() => resolve(false));  // autoplay bloqué
  });
  // Précharge en parallèle TOUS les clips de la question (énoncé + réponses +
  // bonne réponse + fiche) → lecture enchaînée sans latence et fiche prête dès
  // la réponse. Clips ~28 Ko, mis en cache par le navigateur.
  const prefetchTts = () => {
    const base = ttsBase(); if (!base) return;
    const urls = [`${base}/q.mp3`, `${base}/ans.mp3`, `${base}/fiche.mp3`,
      ...(question.answers || []).map((a) => `${base}/${a.audio_key}.mp3`)];
    urls.forEach((u) => { try { fetch(u, { cache: 'force-cache' }).catch(() => {}); } catch (e) {} });
  };
  const readQuestion = async () => {
    stopTts(); // coupe toute lecture en cours avant d'en relancer une (pas d'empilement)
    const base = ttsBase(); if (!base) return;
    const token = ++ttsTokenRef.current;
    setTtsReading(true);
    // L'énoncé d'abord : s'il est introuvable (audio du thème pas dispo), on
    // ARRÊTE proprement → pas de défilement du glow sur les 4 réponses, pas de
    // flood de 404. Sinon on réchauffe le reste des clips et on enchaîne.
    const ok = await playClip(`${base}/q.mp3`);
    if (!ok) { if (token === ttsTokenRef.current) setTtsReading(false); return; }
    prefetchTts();
    for (const ans of (question.answers || [])) {
      if (token !== ttsTokenRef.current) return;   // question suivante → on arrête
      setGlowKey(ans.audio_key);
      await playClip(`${base}/${ans.audio_key}.mp3`);
      setGlowKey(null);
    }
    if (token === ttsTokenRef.current) setTtsReading(false);
  };
  const readResult = async (correctAnswerId, isCorrect, connections) => {
    stopTts(); // coupe toute lecture en cours avant d'en relancer une (pas d'empilement)
    const base = ttsBase(); if (!base) return;
    const token = ++ttsTokenRef.current;
    const correct = (question.answers || []).find((a) => a.id === correctAnswerId);
    if (correct) setGlowKey(correct.audio_key);     // glow la bonne réponse pendant l'annonce
    const ok = await playClip(`${base}/ans.mp3`);
    if (!ok) { if (token === ttsTokenRef.current) setGlowKey(null); return; }  // audio indispo → on arrête
    await new Promise((r) => setTimeout(r, 400));    // courte pause avant la fiche
    if (token !== ttsTokenRef.current) return;
    setGlowKey(null);
    if (ficheVisibleFor(isCorrect)) await playClip(`${base}/fiche.mp3`);
    // Connexions découvertes : on lit chaque clip /tts/conn/<key>.mp3 après la fiche.
    for (const c of (connections || [])) {
      if (token !== ttsTokenRef.current) return;
      if (c && c.audio_key) await playClip(`/tts/conn/${c.audio_key}.mp3`);
    }
  };
  // Lecture COMPLÈTE (bouton « Écouter ») : énoncé + réponses, puis — si déjà
  // répondu — la bonne réponse + la fiche. Coupe toute lecture en cours.
  const readAll = async () => {
    stopTts();
    const base = ttsBase(); if (!base) return;
    const token = ++ttsTokenRef.current;
    setTtsReading(true);
    const ok = await playClip(`${base}/q.mp3`);
    if (!ok) { if (token === ttsTokenRef.current) setTtsReading(false); return; }
    prefetchTts();
    for (const ans of (question.answers || [])) {
      if (token !== ttsTokenRef.current) return;
      setGlowKey(ans.audio_key);
      await playClip(`${base}/${ans.audio_key}.mp3`);
      setGlowKey(null);
    }
    if (result) {
      if (token !== ttsTokenRef.current) return;
      const correct = (question.answers || []).find((a) => a.id === result.correct_answer_id);
      if (correct) setGlowKey(correct.audio_key);
      await playClip(`${base}/ans.mp3`);
      if (token !== ttsTokenRef.current) { setGlowKey(null); return; }
      await new Promise((r) => setTimeout(r, 300));
      setGlowKey(null);
      if (token !== ttsTokenRef.current) return;
      await playClip(`${base}/fiche.mp3`);
    }
    if (token === ttsTokenRef.current) setTtsReading(false);
  };
  // auto-lecture à chaque nouvelle question (si activé)
  useEffect(() => {
    if (question && audioOn && !result) readQuestion();
    return () => stopTts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [question?.id]);

  // Force scroll-to-top à chaque nouvelle question (iOS WebView ne respecte pas window.scrollTo seul)
  useEffect(() => {
    if (question) {
      const doScroll = () => {
        window.scrollTo(0, 0);
        document.documentElement.scrollTop = 0;
        document.body.scrollTop = 0;
        const root = document.getElementById('root');
        if (root) root.scrollTop = 0;
        const app = document.querySelector('.app');
        if (app) app.scrollTop = 0;
      };
      doScroll();
      // Re-scroller après un frame, puis après 100ms (pour laisser AnimatePresence finir)
      requestAnimationFrame(doScroll);
      const t1 = setTimeout(doScroll, 100);
      const t2 = setTimeout(doScroll, 300);
      return () => { clearTimeout(t1); clearTimeout(t2); };
    }
  }, [question?.id]);

  useEffect(() => {
    if (!question && sessionId) {
      api.getQuestion(sessionId)
        .then(q => { setQuestion(q); setLoading(false); })
        .catch(() => navigate('/'));
    }
  }, [sessionId, question, navigate]);

  const handleAnswer = async (answerId) => {
    if (selectedAnswer) return;
    stopTts(); // coupe la lecture en cours (question/réponses) dès qu'on répond
    setSelectedAnswer(answerId);
    const timeSpent = Date.now() - questionStart;
    try {
      const res = await api.submitAnswer({
        session_id: sessionId, question_id: question.id,
        answer_id: answerId, time_spent_ms: timeSpent,
      });
      setResult(res);
      if (audioOn) readResult(res.correct_answer_id, res.is_correct, res.new_connections);
      setSession(prev => ({ ...prev, current_index: res.session_progress, correct_answers: res.session_correct }));
      
      // Points from backend
      if (res.points_earned > 0) {
        setTotalPoints(res.total_week_points);
        setLastPoints({ points: res.points_earned, bonus: 0 });
      } else {
        setLastPoints(null);
      }

      // Nouvelles connexions : juste le texte inline (comme en duel), plus de popup.
      if (res.new_connections && res.new_connections.length > 0) {
        setNewConnections(res.new_connections);
      } else {
        setNewConnections([]);
      }
      // Completed missions
      if (res.completed_missions && res.completed_missions.length > 0) {
        setCompletedMissions(res.completed_missions);
      } else {
        setCompletedMissions([]);
      }
      // New achievements
      if (res.new_achievements && res.new_achievements.length > 0) {
        setNewAchievements(res.new_achievements);
      } else {
        setNewAchievements([]);
      }
      if (res.is_correct) {
        const newStreak = streak + 1;
        setStreak(newStreak);
        if (newStreak > bestStreak) setBestStreak(newStreak);
        // Track streak mission (fire and forget)
        if (newStreak >= 2) {
          api.request('POST', '/missions/track-streak', { streak: newStreak }).catch(() => {});
        }
        if ((newStreak >= 3 || timeSpent < 3000) && !res.new_connections?.length) {
          setShowConfetti(true);
          setTimeout(() => setShowConfetti(false), 2000);
        }
      } else {
        setStreak(0); setLastPoints(null);
        setShakeWrong(true);
        setTimeout(() => setShakeWrong(false), 500);
      }
    } catch (err) { console.error(err); }
  };

  // Keep ref updated for timer callback
  handleAnswerRef.current = handleAnswer;

  // When timer hits 0 — auto-submit wrong answer
  const handleTimeExpire = React.useCallback(() => {
    if (selectedAnswer || result) return;
    setTimedOut(true);
    // Use ref to call the latest handleAnswer
    const answers = question?.answers;
    if (!answers) return;
    // Pick the first wrong answer
    const wrong = answers.find(a => !a.is_correct) || answers[0];
    if (wrong && handleAnswerRef.current) {
      handleAnswerRef.current(wrong.id);
    }
  }, [selectedAnswer, result, question]);

  const nextQuestion = async () => {
    stopTts();
    const totalQ = session?.total_questions || 10;
    if ((result?.session_progress || 0) >= totalQ) {
      navigate(`/summary/${sessionId}`, { state: { totalPoints, bestStreak }, replace: true });
      return;
    }
    setSelectedAnswer(null); setResult(null); setLastPoints(null); setNewConnections([]); setShowConnectionPopup(false); setConnIndex(0); setCompletedMissions([]); setTimedOut(false); setReportSent(false); setReporting(false); setShowReportInput(false); setReportReason('');
    setLoading(true); setQuestionKey(k => k + 1); setQuestionStart(Date.now());
    // Reset scroll on all possible scroll containers (iOS WebView is tricky)
    requestAnimationFrame(() => {
      window.scrollTo({ top: 0, behavior: 'instant' });
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
      const root = document.getElementById('root');
      if (root) root.scrollTop = 0;
      const app = document.querySelector('.app');
      if (app) app.scrollTop = 0;
      // Tous les éléments avec overflow auto/scroll
      document.querySelectorAll('[class*="page"], .scrollable').forEach(el => {
        el.scrollTop = 0;
      });
    });
    try {
      const q = await api.getQuestion(sessionId);
      setQuestion(q);
    } catch {
      navigate(`/summary/${sessionId}`, { state: { totalPoints, bestStreak }, replace: true });
    }
    setLoading(false);
  };

  if (loading || !question) {
    return <div className="loading-screen"><div className="loading-rocket">🚀</div><p>Chargement...</p></div>;
  }

  const totalQ = session?.total_questions || 10;
  const progressNum = result ? result.session_progress : (session?.current_index || 0);
  const correctNum = result ? result.session_correct : (session?.correct_answers || 0);

  return (
    <motion.div initial={{ opacity:0 }} animate={{ opacity:1 }} exit={{ opacity:0 }}>
      <Confetti active={showConfetti} />
      <nav className="nav quiz-nav">
        <div className="nav-logo" onClick={() => navigate('/')}>{session?.theme ? `${session.theme.icon || '📖'} ${session.theme.name}` : 'Curiosaurus'}</div>
        <div className="quiz-progress">
          <div className="progress-bar">
            <motion.div className="progress-fill" animate={{ width:`${progressNum/totalQ*100}%` }}
              transition={{ duration:0.3 }} />
          </div>
          <span className="progress-text">{progressNum}/{totalQ}</span>
        </div>
        <div className="quiz-header-stats">
          <span className="quiz-score-badge">✅ {correctNum}</span>
          <span className="quiz-points-badge">{totalPoints} pts</span>
        </div>
      </nav>

      {hasTimer && <TimerBar active={!result && !loading && !showAdaptiveTip} duration={25} onExpire={handleTimeExpire} />}

      <div className="streak-container">
        <StreakBadge streak={streak} />
      </div>

      <div className="container">
        <AnimatePresence mode="wait">
          <motion.div key={questionKey}
            initial={{ opacity:0,x:40 }} animate={{ opacity:1,x:0 }}
            exit={{ opacity:0,x:-40 }} transition={{ duration:0.25 }}
            className={shakeWrong ? 'shake' : ''}>
            <div className="question-card">
              <div className="question-top-row">
                <span className={`question-difficulty diff-${question.difficulty}`}>
                  {question.difficulty === 'decouverte' ? '🐣 Découverte' :
                   question.difficulty === 'curieux' ? '🔍 Curieux' :
                   question.difficulty === 'explorateur' ? '🧭 Explorateur' : '⭐ Expert'}
                </span>
                <span className="question-number">Q{progressNum + 1}</span>
              </div>
              <QuestionImage imageUrl={question.image_url} />
              <div className="question-text">{question.text}</div>
            </div>

            {!audioOn && question.qid && (
              <button type="button" className="tts-replay-btn"
                onClick={() => readAll()}
                aria-label="Écouter la question">🔊 Écouter</button>
            )}
            <div className="answers-grid">
              {question.answers.map((answer, i) => {
                let cls = 'answer-btn';
                if (!result && glowKey && glowKey === answer.audio_key) cls += ' tts-glow';
                if (result) {
                  if (answer.id === result.correct_answer_id) cls += ' correct';
                  // Timeout : on ne met AUCUNE réponse en rouge (on ne fait que révéler la
                  // bonne). La soumission serveur reste une mauvaise réponse pour le score.
                  else if (answer.id === selectedAnswer && !timedOut) cls += ' wrong';
                  else cls += ' dimmed';
                }
                return (
                  <motion.button key={answer.id} className={cls}
                    onClick={() => handleAnswer(answer.id)} disabled={!!selectedAnswer}
                    initial={{ opacity:1,y:0 }} animate={{ opacity:1,y:0 }}
                    style={{ touchAction: 'manipulation' }}
                    whileTap={!selectedAnswer ? { scale:0.97 } : {}}>
                    <span className="answer-letter">{String.fromCharCode(65+i)}</span>
                    <span>{answer.text}</span>
                  </motion.button>
                );
              })}
            </div>

            <AnimatePresence>
              {result && lastPoints && (
                <motion.div className="points-popup"
                  initial={{ opacity:0,y:20,scale:0.5 }} animate={{ opacity:1,y:-20,scale:1 }}
                  exit={{ opacity:0,y:-50 }} transition={{ duration:0.6 }}>
                  <span className="points-value">+{lastPoints.points}</span>
                  {lastPoints.bonus > 0 && <span className="points-bonus">+{lastPoints.bonus} combo</span>}
                </motion.div>
              )}
            </AnimatePresence>

            {result && (
              <motion.div className="feedback" initial={{ opacity:0,y:20 }} animate={{ opacity:1,y:0 }}
                transition={{ delay:0.1 }}>
                <div className="feedback-header">
                  <div className="feedback-icon">{result.is_correct ? '🎉' : '💫'}</div>
                  <div className={`feedback-title ${result.is_correct ? 'correct' : 'wrong'}`}>
                    {result.is_correct
                      ? (streak >= 5 ? 'INCROYABLE !' : streak >= 3 ? 'Fantastique !' : 'Bravo !')
                      : timedOut ? '⏰ Temps écoulé !' : 'Pas tout à fait...'}
                  </div>
                </div>
                {result.explanation && ficheVisibleFor(result.is_correct) && <div className="feedback-explanation">{result.explanation}</div>}
                <button className="btn btn-primary next-btn" onClick={nextQuestion}>
                  {result.session_progress >= totalQ ? '🏆 Voir les résultats' : '➡️ Question suivante'}
                </button>
                {result.fact_card && ficheVisibleFor(result.is_correct) && (
                  <div className="fact-card">
                    <div className="fact-card-badge">
                      {result.is_new_card ? '✨ Nouvelle fiche !' : '📚 Déjà collectée'}
                    </div>
                    <h4>💡 {result.fact_card.title}</h4>
                    <p>{result.fact_card.content}</p>
                  </div>
                )}

                {/* Connexions découvertes : texte inline (comme en duel) */}
                {newConnections.length > 0 && newConnections.map((conn, i) => (
                  <motion.div key={i} className="connection-teaser"
                    initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                    {conn.tag} Connexion : <strong>{conn.card_a_title}</strong> ({conn.card_a_theme})
                    ↔ <strong>{conn.card_b_title}</strong> ({conn.card_b_theme})
                    {conn.explanation && <div style={{ fontSize: '0.85rem', marginTop: 4, opacity: 0.8 }}>{conn.explanation}</div>}
                  </motion.div>
                ))}

                {/* Mission completed */}
                {completedMissions.length > 0 && completedMissions.map((cm, i) => (
                  <motion.div key={i} className="mission-completed-banner"
                    initial={{ opacity: 0, scale: 0.7, y: 10 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    transition={{ delay: 0.8 + i * 0.3, type: 'spring', damping: 10 }}>
                    <span className="mission-completed-icon">{cm.icon}</span>
                    <div className="mission-completed-info">
                      <span className="mission-completed-label">Mission accomplie !</span>
                      <span className="mission-completed-title">{cm.title}</span>
                      {cm.description && <span className="mission-completed-desc">{cm.description}</span>}
                    </div>
                    <span className="mission-completed-bonus">+{cm.bonus_points} pts</span>
                  </motion.div>
                ))}

                {/* Achievement unlocked */}
                {newAchievements.length > 0 && newAchievements.map((ach, i) => (
                  <motion.div key={`ach-${i}`} className="mission-completed-banner"
                    style={{ background: 'linear-gradient(135deg, rgba(255,215,0,0.15), rgba(255,107,0,0.1))', borderColor: 'rgba(255,215,0,0.3)' }}
                    initial={{ opacity: 0, scale: 0.7, y: 10 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    transition={{ delay: 1.0 + i * 0.3, type: 'spring', damping: 10 }}>
                    <span className="mission-completed-icon">{ach.icon}</span>
                    <div className="mission-completed-info">
                      <span className="mission-completed-label" style={{ color: '#f2c063' }}>🏆 Haut fait !</span>
                      <span className="mission-completed-title">{ach.title}</span>
                      {ach.description && <span className="mission-completed-desc">{ach.description}</span>}
                    </div>
                    <span className="mission-completed-bonus" style={{ color: '#f2c063' }}>+{ach.stars} pts</span>
                  </motion.div>
                ))}

                {/* Redemption bonus - answered correctly after previous failure */}
                {result.redemption_bonus > 0 && (
                  <motion.div
                    style={{
                      background: 'linear-gradient(135deg, #00c864, #00a854)',
                      borderRadius: 14, padding: '16px 20px', marginTop: 12,
                      display: 'flex', alignItems: 'center', gap: 14,
                      boxShadow: '0 0 20px rgba(0,200,100,0.3)',
                    }}
                    initial={{ opacity: 0, scale: 0.7 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.3, type: 'spring', damping: 10 }}>
                    <span style={{ fontSize: '2rem' }}>🧠</span>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 900, fontSize: '1.1rem', color: '#fff' }}>
                        Revanche réussie !
                      </div>
                      <div style={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.85)' }}>
                        Tu t'étais trompé avant, mais tu as retenu la réponse !
                      </div>
                    </div>
                    <div style={{
                      background: 'rgba(255,255,255,0.2)', borderRadius: 10, padding: '8px 14px',
                      fontWeight: 900, fontSize: '1.2rem', color: '#fff',
                    }}>+{result.redemption_bonus} pts</div>
                  </motion.div>
                )}
                <button
                  className="report-question-btn"
                  onClick={() => setShowReportInput(true)}
                  disabled={reportSent}>
                  {reportSent ? '✅ Question signalée, merci !' : '🚩 Signaler cette question'}
                </button>
              </motion.div>
            )}
          </motion.div>
        </AnimatePresence>

        {showReportInput && (
          <div className="report-modal-overlay" onClick={() => { if (!reporting) { setShowReportInput(false); setReportReason(''); } }}>
            <div className="report-modal" onClick={(e) => e.stopPropagation()}>
              <ModalClose onClick={() => { setShowReportInput(false); setReportReason(''); }} />
              <h3 className="report-modal-title">🚩 Signaler cette question</h3>
              <p className="report-modal-sub">Dis-nous ce qui ne va pas (obligatoire) :</p>
              <textarea
                className="report-textarea"
                value={reportReason}
                onChange={(e) => setReportReason(e.target.value)}
                placeholder="Ex : l'image ne correspond pas, la réponse est fausse, faute d'orthographe…"
                rows={4}
                autoFocus
              />
              <div className="report-box-actions">
                <button
                  className="report-cancel-btn"
                  onClick={() => { setShowReportInput(false); setReportReason(''); }}
                  disabled={reporting}>
                  Annuler
                </button>
                <button
                  className="report-send-btn"
                  onClick={submitReport}
                  disabled={reporting || !reportReason.trim()}>
                  {reporting ? '⏳ Envoi…' : 'Envoyer'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* (popup connexion plein écran retirée — le texte de connexion s'affiche
          désormais inline dans le feedback, comme en duel) */}

      {showAdaptiveTip && (
        <OnboardingModal
          emoji="🎯"
          title="Curio s'adapte à toi !"
          buttonLabel="C'est parti ! 🚀"
          onClose={dismissAdaptiveTip}
        >
          Les premières questions sont <strong>faciles</strong>, pour bien démarrer. Plus tu réponds bien, plus elles deviennent <strong>intéressantes et un peu plus corsées</strong> — Curio règle la difficulté <strong>rien que pour toi</strong>, à ton rythme.
          <br /><br />
          Alors lance-toi : <strong>il n'y a pas de mauvaise façon de commencer !</strong>
        </OnboardingModal>
      )}

    </motion.div>
  );
}
